import { useId, useState } from 'react';

import { formatViews } from '@/features/media/library';

const W = 120;
const H = 32;
const PAD = 4;

const dayFormat = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' });

/**
 * A single-series trend line (dataviz: a 2px line in the de-emphasis ink,
 * the latest point in the accent, no legend - the row names it). Days
 * without a measurement are gaps, never zeros. Hovering reads the day's
 * value; the aria-label says the same in words.
 */
export function Sparkline({
  points,
  label,
}: {
  points: { day: string; followers: number | null }[];
  /** What it shows, e.g. "Followers, last 30 days". */
  label: string;
}) {
  const id = useId();
  const [hover, setHover] = useState<number | null>(null);
  const known = points.flatMap((p, i) =>
    p.followers === null ? [] : [{ i, v: p.followers }],
  );
  if (known.length === 0) {
    return <span className="text-caption text-text-tertiary">No trend yet</span>;
  }
  const values = known.map((k) => k.v);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const x = (i: number) => PAD + (i / Math.max(points.length - 1, 1)) * (W - PAD * 2);
  const y = (v: number) =>
    max === min ? H / 2 : PAD + (1 - (v - min) / (max - min)) * (H - PAD * 2);

  // Runs of consecutive known days, drawn as separate lines so gaps stay gaps.
  const runs: { i: number; v: number }[][] = [];
  for (const point of known) {
    const run = runs.at(-1);
    if (run && run.at(-1)!.i === point.i - 1) run.push(point);
    else runs.push([point]);
  }
  const last = known.at(-1)!;
  const first = known[0]!;
  const shown = hover === null ? null : points[hover];

  return (
    <span className="relative inline-flex">
      <svg
        width={W}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-labelledby={id}
        onPointerMove={(event) => {
          const box = event.currentTarget.getBoundingClientRect();
          const ratio = (event.clientX - box.left) / box.width;
          setHover(Math.round(ratio * (points.length - 1)));
        }}
        onPointerLeave={() => setHover(null)}
      >
        <title id={id}>
          {`${label}: ${formatViews(first.v)} on ${dayFormat.format(new Date(points[first.i]!.day))}, ${formatViews(last.v)} on ${dayFormat.format(new Date(points[last.i]!.day))}`}
        </title>
        {runs.map((run) =>
          run.length === 1 ? (
            <circle
              key={run[0]!.i}
              cx={x(run[0]!.i)}
              cy={y(run[0]!.v)}
              r={1.5}
              className="fill-icon-tertiary"
            />
          ) : (
            <polyline
              key={run[0]!.i}
              points={run.map((p) => `${x(p.i)},${y(p.v)}`).join(' ')}
              fill="none"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="stroke-icon-tertiary"
            />
          ),
        )}
        {hover !== null && (
          <line
            x1={x(hover)}
            x2={x(hover)}
            y1={0}
            y2={H}
            strokeWidth={1}
            className="stroke-border-subtle"
          />
        )}
        <circle cx={x(last.i)} cy={y(last.v)} r={3} className="fill-brand-strong" />
      </svg>
      {shown && (
        <span className="bg-bg-inverse text-caption text-text-inverse pointer-events-none absolute -top-7 left-1/2 -translate-x-1/2 rounded-md px-2 py-0.5 whitespace-nowrap shadow-sm">
          {dayFormat.format(new Date(shown.day))}:{' '}
          {shown.followers === null ? 'not measured' : formatViews(shown.followers)}
        </span>
      )}
    </span>
  );
}
